// Socle PASSIF du diagnostic : ce que le navigateur expose nativement, sans
// patcher quoi que ce soit dans l'app du client.
//
// Pourquoi ce module existe à côté de `diagnostics-capture.ts` (qui, lui,
// monkey-patche) : le patch de `console.*` est incontournable — il n'existe
// aucune API d'observation de la console. Le patch réseau, lui, ne l'est pas, et
// il est à la fois le plus invasif et le MOINS couvrant :
//  - il ne voit que `fetch` et XHR, jamais les images, scripts, feuilles de
//    style ou polices — un chunk JS en 404 est pourtant un bug de test banal ;
//  - il entre en concurrence avec Sentry / Datadog, qui wrappent aussi `fetch`
//    chez le client ;
//  - il ne peut rien capturer avant son installation, là où
//    `PerformanceObserver` rejoue l'historique avec `buffered: true`.
//
// Le wrapper reste nécessaire pour le statut HTTP et la méthode (Resource
// Timing ne les expose pas partout) : les deux sources sont complémentaires,
// et on déduplique par `initiatorType`.

import { safeUrl } from "./diagnostics-redact";
import type { NetworkEntry } from "./network-ring";

/** Requêtes déjà couvertes par le wrapper `fetch`/XHR, avec leur vrai statut :
 *  on ignore leur double Resource Timing plutôt que de dégrader l'information. */
const WRAPPED_INITIATORS = new Set(["fetch", "xmlhttprequest"]);

/** Rapports du navigateur qu'on juge utiles à un testeur. `crash` est exclu :
 *  il n'arriverait de toute façon jamais jusqu'à l'envoi du rapport. */
const REPORT_TYPES = ["deprecation", "intervention"];

export type ObserverSinks = {
  /** Une requête observée passivement (jamais une entrée du wrapper). */
  onResource: (entry: NetworkEntry) => void;
  /** Un signal du navigateur : dépréciation, intervention, violation CSP. */
  onReport: (level: "error" | "warn", message: string) => void;
};

export type ObserverHandle = { teardown: () => void };

const NOOP: ObserverHandle = { teardown: () => {} };

/** Horodatage epoch d'une entrée Performance (dont `startTime` est relatif à
 *  `timeOrigin`), pour rester sur la même échelle que les `Date.now()` du
 *  wrapper — sinon le tri chronologique côté admin mélangerait tout. */
function epochOf(startTime: number): number {
  const origin = typeof performance !== "undefined" ? performance.timeOrigin : 0;
  return Math.round(origin + startTime);
}

/** Statut HTTP d'une entrée Resource Timing. `responseStatus` n'existe pas
 *  partout (et vaut 0 pour un cross-origin sans `Timing-Allow-Origin`) : on
 *  préfère l'ABSENCE de statut à un `0` qui signifierait « échec » à tort. */
function statusOf(entry: PerformanceResourceTiming): number | undefined {
  const raw = (entry as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
  return typeof raw === "number" && raw > 0 ? raw : undefined;
}

function toNetworkEntry(entry: PerformanceResourceTiming): NetworkEntry | null {
  if (WRAPPED_INITIATORS.has(entry.initiatorType)) return null;
  const status = statusOf(entry);
  // `transferSize` vaut 0 sur un hit de cache : on retombe sur la taille
  // encodée, qui reste une information utile pour l'équipe.
  const size = entry.transferSize || entry.encodedBodySize || undefined;
  return {
    method: "GET", // Resource Timing ne couvre que des GET (hors fetch/XHR, exclus).
    // MÊME rédaction que le wrapper fetch/XHR : retirer la query ne suffit pas,
    // un identifiant se loge tout aussi bien dans le chemin
    // (/u/jane@corp.com/avatar.png). La garantie pré-egress vaut pour les deux
    // sources, sinon elle ne vaut rien.
    url: safeUrl(entry.name),
    ...(status !== undefined ? { status } : {}),
    durationMs: Math.round(entry.duration),
    ...(size ? { responseSize: size } : {}),
    startedAt: epochOf(entry.startTime),
    ...(status !== undefined && status >= 400 ? { failed: true } : {}),
    initiatorType: entry.initiatorType,
  };
}

/**
 * Installe les observateurs passifs. Chaque bloc est indépendamment gardé :
 * `ReportingObserver` n'existe pas dans Safari, `PerformanceObserver` peut
 * refuser un type inconnu. Un observateur indisponible ne doit jamais empêcher
 * les autres de tourner, ni casser l'app hôte.
 */
export function installDiagnosticObservers(sinks: ObserverSinks): ObserverHandle {
  if (typeof window === "undefined") return NOOP;
  const teardowns: Array<() => void> = [];

  // --- Requêtes réseau, toutes origines confondues -------------------------
  // `buffered: true` rejoue les entrées ANTÉRIEURES à l'observateur : les
  // ressources chargées pendant le démarrage de l'app sont récupérées
  // rétroactivement, ce qu'aucun wrapper ne peut faire.
  if (typeof PerformanceObserver !== "undefined") {
    try {
      const obs = new PerformanceObserver((list) => {
        for (const raw of list.getEntries()) {
          try {
            const entry = toNetworkEntry(raw as PerformanceResourceTiming);
            if (entry) sinks.onResource(entry);
          } catch {
            // Observation best-effort : jamais au détriment de l'app hôte.
          }
        }
      });
      obs.observe({ type: "resource", buffered: true });
      teardowns.push(() => obs.disconnect());
    } catch {
      // Type non supporté : on s'en passe.
    }
  }

  // --- Signaux du navigateur (dépréciations, interventions) ----------------
  const ReportingObserverCtor = (
    window as unknown as { ReportingObserver?: new (...a: never[]) => ReportingObserverLike }
  ).ReportingObserver;
  if (ReportingObserverCtor) {
    try {
      const obs = new ReportingObserverCtor(
        ((reports: BrowserReport[]) => {
          for (const r of reports) {
            const detail = typeof r.body?.message === "string" ? r.body.message : r.type;
            sinks.onReport("warn", `Navigateur (${r.type}) : ${detail}`);
          }
        }) as never,
        { types: REPORT_TYPES, buffered: true } as never,
      );
      obs.observe();
      teardowns.push(() => obs.disconnect());
    } catch {
      // Indisponible : on s'en passe.
    }
  }

  // --- Violations CSP ------------------------------------------------------
  // Le classique « ça marche en local, pas en prod » : une ressource bloquée
  // par la politique de sécurité échoue sans trace exploitable ailleurs.
  const onCsp = (e: Event) => {
    const v = e as SecurityPolicyViolationEvent;
    sinks.onReport(
      "error",
      `CSP : ${v.blockedURI || "ressource"} bloqué par « ${v.violatedDirective} »`,
    );
  };
  window.addEventListener("securitypolicyviolation", onCsp);
  teardowns.push(() => window.removeEventListener("securitypolicyviolation", onCsp));

  let done = false;
  return {
    teardown: () => {
      if (done) return;
      done = true;
      for (const t of teardowns) {
        try {
          t();
        } catch {
          // Déconnexion best-effort.
        }
      }
    },
  };
}

// `ReportingObserver` n'est pas dans les lib DOM de toutes les versions de TS :
// on décrit le strict minimum qu'on utilise plutôt que d'élargir la config.
type BrowserReport = { type: string; body?: { message?: unknown } };
type ReportingObserverLike = { observe: () => void; disconnect: () => void };
